import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, X } from 'lucide-react'
import { useHeroBanners, useMiniBanners, useIndustryTrends, useHomeNotices, usePublicNotices } from '@/hooks/useData'
import type { WebsitePublicNoticeAttachment } from '@/types'

const DEFAULT_HERO_SLIDES = [
  {
    id: 'fallback-hero-1',
    src: '/images/hero-image.jpg',
    alt: '环保技术服务',
    title: '专业环保技术服务',
    subtitle: '绿色治理 · 智慧赋能',
    summary: '依托先进的LDAR检测技术，为企业提供一站式环保解决方案。',
    linkUrl: '/about',
  },
  {
    id: 'fallback-hero-2',
    src: '/images/about-company.png',
    alt: '绿色发展新时代',
    title: '绿色发展新时代',
    subtitle: '协同创新 · 安全合规',
    summary: '助力企业构建精细化环保管理体系，实现高质量发展。',
    linkUrl: '/qualifications',
  },
]

const isExternalLink = (href: string) => /^https?:/i.test(href)

const renderDetailLink = (href: string, className: string, children: React.ReactNode) => {
  if (!href) {
    return <span className={className}>{children}</span>
  }

  if (isExternalLink(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
      </a>
    )
  }

  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  )
}

const formatDate = (input?: string | number | null) => {
  if (input === undefined || input === null) return ''

  const formatDateInstance = (date: Date) => {
    if (Number.isNaN(date.getTime())) return ''
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  }

  const tryParseNumeric = (value: number) => {
    const timestamp = value < 1e12 ? value * 1000 : value
    return formatDateInstance(new Date(timestamp))
  }

  if (typeof input === 'number') {
    return tryParseNumeric(input)
  }

  const value = String(input).trim()
  if (!value) return ''

  if (/^\d+$/.test(value)) {
    return tryParseNumeric(Number(value))
  }

  if (value.includes('T')) {
    const date = new Date(value)
    const formatted = formatDateInstance(date)
    if (formatted) return formatted
    return value.split('T')[0]
  }

  const date = new Date(value)
  const formatted = formatDateInstance(date)
  if (formatted) return formatted

  return value
}

const NEWS_TABS = [
  { key: 'industry', label: '行业动态' },
  { key: 'notice', label: '通知公告' },
  { key: 'public', label: '信息公示' },
] as const

type NewsTabKey = (typeof NEWS_TABS)[number]['key']

type MockNewsItem = {
  id: string
  title: string
  date: string
  summary: string
  linkUrl: string
}

type NormalizedItem = {
  id: string
  title: string
  summary?: string
  publishDate?: string
  linkUrl?: string
  status?: string
  categoryLabel: string
  subtitle?: string
  referenceNo?: string
  department?: string
  attachments?: WebsitePublicNoticeAttachment[]
}

const MOCK_NEWS_DATA: Record<NewsTabKey, MockNewsItem[]> = {
  industry: [
    {
      id: '1',
      title: '新规施行：VOCs治理技术标准再升级',
      date: '2024-06-26',
      summary: '生态环境部发布最新挥发性有机物治理标准，明确石化行业治理要求与时间节点。',
      linkUrl: '/',
    },
  ],
  public: [
    {
      id: '6',
      title: '2024年第一季度排放监测结果公示',
      date: '2024-06-20',
      summary: '按季度公布重点排放节点监测数据，持续保障企业环保透明度。',
      linkUrl: '/',
    },
  ],
  notice: [
    {
      id: '11',
      title: '关于开展夏季安全隐患排查的通知',
      date: '2024-06-18',
      summary: '请各部门按照排查清单逐项落实整改，确保高温季节安全生产。',
      linkUrl: '/',
    },
  ],
}
// import { useCompanyData, useCases, useNews } from '@/hooks/useData'

export default function Home() {
//   const { data: companyData } = useCompanyData()
//   const { data: casesData } = useCases()
//   const { data: newsData } = useNews()

  const { data: heroBannerData = [] } = useHeroBanners()
  const { data: miniBannerData = [] } = useMiniBanners()
  const [activeTab, setActiveTab] = useState<NewsTabKey>('industry')
  const [selectedNotice, setSelectedNotice] = useState<NormalizedItem | null>(null)

  const mockIndustry = useMemo<MockNewsItem[]>(() => MOCK_NEWS_DATA.industry, [])
  const mockNotice = useMemo<MockNewsItem[]>(() => MOCK_NEWS_DATA.notice, [])
  const mockPublic = useMemo<MockNewsItem[]>(() => MOCK_NEWS_DATA.public, [])

  const heroSlides = useMemo(() => {
    if (heroBannerData.length) {
      return heroBannerData.map((item) => ({
        id: String(item.id ?? Math.random()),
        src: item.imageUrl || '/images/hero-image.jpg',
        alt: item.title || '环保技术服务',
        title: item.title,
        subtitle: item.subtitle,
        summary: item.summary,
        linkUrl: item.linkUrl,
      }))
    }
    return DEFAULT_HERO_SLIDES
  }, [heroBannerData])

  const [currentBanner, setCurrentBanner] = useState(0)

  const newsSlides = useMemo(() => {
    const source = miniBannerData.length ? miniBannerData : heroBannerData
    if (source.length) {
      return source.map((item) => ({
        id: String(item.id ?? Math.random()),
        src: item.imageUrl || '/images/company-secret.png',
        alt: item.title || '行业动态',
        title: item.title || '行业资讯速递',
        summary: item.summary || item.subtitle || '聚焦环保政策与行业动态，洞察绿色发展趋势。',
        linkUrl: item.linkUrl,
      }))
    }
    return [
      { id: 'fallback-news-1', src: '/images/company-secret.png', alt: '行业资讯', title: '行业资讯速递', summary: '关注环保政策动向，洞察绿色发展趋势。', linkUrl: '/news' },
      { id: 'fallback-news-2', src: '/images/company-commitments.png', alt: '技术实践', title: '技术实践案例', summary: '以专业能力守护环境安全与健康。', linkUrl: '/news' },
    ]
  }, [miniBannerData, heroBannerData])

  const [currentNewsImage, setCurrentNewsImage] = useState(0)

  useEffect(() => {
    setCurrentBanner(0)
  }, [heroSlides.length])

  useEffect(() => {
    if (heroSlides.length <= 1) return

    const timer = window.setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % heroSlides.length)
    }, 5000)

    return () => window.clearInterval(timer)
  }, [heroSlides])

  useEffect(() => {
    setCurrentNewsImage(0)
  }, [newsSlides.length])

  useEffect(() => {
    if (newsSlides.length <= 1) return

    const timer = window.setInterval(() => {
      setCurrentNewsImage((prev) => (prev + 1) % newsSlides.length)
    }, 6000)

    return () => window.clearInterval(timer)
  }, [newsSlides])

  const { data: industryNews = [] } = useIndustryTrends()

  const normalizedIndustryNews: NormalizedItem[] = useMemo(() => {
    if (industryNews.length) {
      return industryNews.slice(0, 6).map((item) => ({
        id: String(item.id ?? Math.random()),
        title: item.title ?? '行业动态',
        summary: item.summary ?? item.subtitle ?? '',
        publishDate: formatDate(item.publishDate ?? item.createTime),
        linkUrl: item.linkUrl ?? '/',
        categoryLabel: '行业动态',
      }))
    }

    return mockIndustry.map((item) => ({
      id: item.id,
      title: item.title,
      summary: item.summary,
      publishDate: item.date,
      linkUrl: item.linkUrl,
      categoryLabel: '行业动态',
    }))
  }, [industryNews, mockIndustry])

  const { data: homeNoticeData = [] } = useHomeNotices()

  const normalizedNotices: NormalizedItem[] = useMemo(() => {
    if (homeNoticeData.length) {
      return homeNoticeData.slice(0, 6).map((item) => ({
        id: String(item.id ?? Math.random()),
        title: item.title ?? '通知公告',
        summary: item.summary ?? item.subtitle ?? '',
        publishDate: formatDate(item.publishDate ?? item.createTime),
        // linkUrl: item.linkUrl ?? '/news',
        status: item.status,
        categoryLabel: '通知公告',
        subtitle: item.subtitle,
      }))
    }

    return mockNotice.map((item) => ({
      id: item.id,
      title: item.title,
      summary: item.summary,
      publishDate: item.date,
      // linkUrl: '/news',
      status: 'enabled',
      categoryLabel: '通知公告',
      subtitle: '',
    }))
  }, [homeNoticeData, mockNotice])

  const { data: publicNoticeData = [] } = usePublicNotices()

  const normalizedPublic: NormalizedItem[] = useMemo(() => {
    if (publicNoticeData.length) {
      return publicNoticeData.slice(0, 6).map((item) => ({
        id: String(item.id ?? Math.random()),
        title: item.title ?? '信息公示',
        summary: item.summary ?? '',
        publishDate: formatDate(item.publishDate ?? item.createTime),
        linkUrl: '/disclosure',
        categoryLabel: item.category ?? '信息公示',
        referenceNo: item.referenceNo,
        department: item.department,
        attachments: item.attachments,
      }))
    }

    return mockPublic.map((item) => ({
      id: item.id,
      title: item.title,
      summary: item.summary,
      publishDate: item.date,
      linkUrl: '/disclosure',
      categoryLabel: '信息公示',
      referenceNo: '',
      department: '',
      attachments: [],
    }))
  }, [publicNoticeData, mockPublic])

  const featuredNews = useMemo(() => {
    const groups = [normalizedIndustryNews, normalizedNotices, normalizedPublic]
    const selections: NormalizedItem[] = []

    groups.forEach((group) => {
      if (group.length) {
        const randomIndex = Math.floor(Math.random() * group.length)
        selections.push(group[randomIndex])
      }
    })

    if (selections.length) {
      return selections
    }

    return normalizedIndustryNews.slice(0, 3)
  }, [normalizedIndustryNews, normalizedPublic, normalizedNotices])

  const currentNewsSlide = newsSlides[currentNewsImage % newsSlides.length] ?? newsSlides[0]

  const tabs = useMemo(() => {
    const list: Array<{ key: NewsTabKey; label: string; items: NormalizedItem[] }> = [
      { key: 'industry', label: '行业动态', items: normalizedIndustryNews },
      { key: 'notice', label: '通知公告', items: normalizedNotices },
      { key: 'public', label: '信息公示', items: normalizedPublic },
    ]
    return list.filter((tab) => tab.items.length > 0)
  }, [normalizedIndustryNews, normalizedNotices, normalizedPublic])

  const activeTabItems = useMemo(() => {
    const current = tabs.find((tab) => tab.key === activeTab)
    return current?.items ?? []
  }, [tabs, activeTab])

  useEffect(() => {
    if (activeTab !== 'notice') {
      setSelectedNotice(null)
    }
  }, [activeTab])

  return (
    <div className="min-h-screen">
      {/* Banner Carousel */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative h-[220px] sm:h-[300px] md:h-[360px] lg:h-[420px] overflow-hidden rounded-2xl shadow-lg">
            {heroSlides.map((banner, index) => {
              const imageElement = (
                <img src={banner.src} alt={banner.alt} className="w-full h-full object-cover" />
              )

              return (
                <div
                  key={banner.id}
                  className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                    index === currentBanner ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {banner.linkUrl ? (
                    isExternalLink(banner.linkUrl) ? (
                      <a href={banner.linkUrl} target="_blank" rel="noopener noreferrer" className="block h-full">
                        {imageElement}
                      </a>
                    ) : (
                      <Link to={banner.linkUrl} className="block h-full">
                        {imageElement}
                </Link>
                    )
                  ) : (
                    imageElement
                  )}
                  {(banner.title || banner.summary) && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent flex items-end">
                      <div className="p-6 sm:p-10 text-white max-w-3xl">
                        {banner.subtitle && (
                          <p className="text-xs uppercase tracking-[0.4em] text-white/70 mb-2">{banner.subtitle}</p>
                        )}
                        {banner.title && <h2 className="text-2xl sm:text-3xl font-semibold mb-2">{banner.title}</h2>}
                        {banner.summary && <p className="text-sm sm:text-base text-white/80 leading-relaxed">{banner.summary}</p>}
              </div>
            </div>
                  )}
                </div>
              )
            })}
            {heroSlides.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {heroSlides.map((banner, index) => (
                  <button
                    key={`${banner.id}-indicator`}
                    aria-label={`切换到第${index + 1}张banner`}
                    className={`h-2 w-6 rounded-full transition-colors duration-300 ${
                      index === currentBanner ? 'bg-white/90' : 'bg-white/30 hover:bg-white/60'
                    }`}
                    onClick={() => setCurrentBanner(index)}
                  />
                ))}
            </div>
            )}
          </div>
        </div>
      </section>

      {/* 新闻资讯区块 */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
            <div>
              <div className="relative h-64 md:h-72 lg:h-80 overflow-hidden rounded-xl shadow-lg">
                {newsSlides.map((image, index) => (
                  <div
                    key={image.id}
                    className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                      index === currentNewsImage ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/20 to-transparent" />
          </div>
                ))}
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <p className="text-xs uppercase tracking-[0.4em] text-white/60">INSIGHT</p>
                  <h3 className="text-2xl font-semibold mt-2">
                    {currentNewsSlide?.title ?? '行业资讯速递'}
                  </h3>
                  <p className="mt-2 text-white/80 max-w-sm text-sm">
                    {currentNewsSlide?.summary ?? '关注环保行业政策与技术动态，助力企业绿色高质量发展。'}
                  </p>
                  {currentNewsSlide?.linkUrl && (
                    <div className="mt-4">
                      {renderDetailLink(
                        currentNewsSlide.linkUrl,
                        'inline-flex items-center gap-2 rounded-full border border-white/70 px-4 py-2 text-sm font-semibold text-white hover:bg-white hover:text-secondary-900 transition-colors',
                        <>
                          查看详情
                          <ExternalLink className="h-4 w-4" />
                        </>
                      )}
            </div>
                  )}
              </div>
                {newsSlides.length > 1 && (
                  <div className="absolute top-4 right-4 flex gap-2">
                    {newsSlides.map((image, index) => (
                      <button
                        key={`${image.id}-news-indicator`}
                        aria-label={`切换到第${index + 1}张新闻图片`}
                        className={`h-1.5 w-6 rounded-full transition-colors duration-300 ${
                          index === currentNewsImage ? 'bg-white/90' : 'bg-white/40 hover:bg-white/70'
                        }`}
                        onClick={() => setCurrentNewsImage(index)}
                      />
                    ))}
              </div>
                )}
              </div>
            </div>
            <div className="flex flex-col">
              <div className="bg-secondary-50 rounded-xl shadow-sm p-6 flex-1">
                <div className="space-y-5">
                  {featuredNews.map((item) => (
                    <div
                      key={`${item.categoryLabel}-${item.id}-featured`}
                      className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-secondary-200 pb-5 last:border-0 last:pb-0"
                    >
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm text-secondary-500">
                          <span className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-600 rounded-full text-xs font-medium">
                            {item.categoryLabel}
                    </span>
                          <span>{item.publishDate}</span>
                        </div>
                        <h4 className="mt-1.5 text-base md:text-lg font-semibold text-primary-800 hover:text-primary-600 transition-colors line-clamp-1 leading-snug">
                          {item.title}
                        </h4>
                        <p className="mt-1 text-xs md:text-sm text-secondary-600 leading-snug line-clamp-1">
                          {item.summary}
                        </p>
                </div>
                {item.linkUrl && (
                  <Link
                    to={item.linkUrl}
                    className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                  >
                    详情
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
            </div>
          </div>

          <div className="mt-10 bg-white border border-secondary-200 rounded-xl shadow-sm">
            <div className="flex flex-wrap border-b border-secondary-200">
              {tabs.length === 0 && (
                <span className="px-4 py-3 text-sm text-secondary-500">暂无资讯数据</span>
              )}
              {tabs.map((tab) => (
                <button
                  key={`${tab.key}-panel`}
                  className={`flex-1 min-w-[140px] px-4 py-3 text-sm font-semibold transition-colors duration-200 ${
                    activeTab === tab.key
                      ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                      : 'text-secondary-600 hover:text-primary-600'
                  }`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
            ))}
          </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {tabs.length === 0 ? (
                <div className="col-span-full text-center text-secondary-500 text-sm">暂无相关内容。</div>
              ) : (
                activeTabItems.map((item) => {
                  const detailHref = item.linkUrl ?? (item.categoryLabel === '信息公示' ? '/disclosure' : '/#')
                  const isNoticeTab = item.categoryLabel === '通知公告'
                  return (
                    <div
                      key={`${item.id}-panel`}
                      className="p-4 rounded-lg bg-secondary-50 hover:bg-primary-50 transition-colors duration-200"
                    >
                      <div className="flex items-center justify-between text-sm text-secondary-500">
                        <span>{item.publishDate ?? '—'}</span>
                        {isNoticeTab ? (
                          <button
                            type="button"
                            onClick={() => setSelectedNotice(item)}
                            className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-semibold text-primary-600 hover:bg-primary-50"
                          >
                            查看详情
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-primary-600">
                            {renderDetailLink(
                              detailHref,
                              'inline-flex items-center gap-1 text-primary-600 hover:text-primary-700',
                              <>
                                查看详情
                                <ExternalLink className="h-4 w-4" />
                              </>
                            )}
                          </span>
                        )}
                      </div>
                      <h4 className="mt-2 text-base font-semibold text-primary-700 line-clamp-2">{item.title}</h4>
                      {item.summary && (
                        <p className="mt-2 text-sm text-secondary-600 leading-relaxed line-clamp-3">{item.summary}</p>
                      )}
                     </div>
                   )
                 })
              )}
        </div>
          </div>
        </div>
      </section>

      {selectedNotice && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onClick={() => setSelectedNotice(null)}
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-primary-500">通知公告</p>
                <h3 className="mt-2 text-2xl font-semibold text-secondary-900">{selectedNotice.title}</h3>
                {selectedNotice.subtitle && (
                  <p className="mt-1 text-sm text-secondary-500">{selectedNotice.subtitle}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelectedNotice(null)}
                className="text-secondary-400 hover:text-secondary-600"
                aria-label="关闭公告详情"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 space-y-3 text-sm text-secondary-600 leading-relaxed">
              <div className="flex flex-wrap gap-4 text-secondary-500">
                <span>发布日期：{selectedNotice.publishDate ?? '—'}</span>
              </div>
              {selectedNotice.summary && <p className="text-secondary-700 whitespace-pre-line">{selectedNotice.summary}</p>}
              {selectedNotice.linkUrl && (
                <div className="pt-2">
                  {renderDetailLink(
                    selectedNotice.linkUrl,
                    'inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700',
                    <>
                      查看原文
                      <ExternalLink className="h-4 w-4" />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  )
} 